import React, { useCallback, useEffect, Suspense, useState } from 'react';
import { TenantBranding } from '../types/branding';

// Lazy components
const BrandPreview = React.lazy(() => import('../components/BrandPreview'));
const BrandEditor = React.lazy(() => import('../components/BrandEditor'));

export default function BrandingPage() {
    const [branding, setBranding] = useState<TenantBranding | null>(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [etag, setEtag] = useState<string | null>(null);
    const [enabled, setEnabled] = useState<boolean>(true);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const { apiFetch } = await import('../api/client');
            // feature
            const fRes = await apiFetch('/api/tenant/self/features');
            if (fRes.ok) { const f = await fRes.json(); setEnabled(!!f.brandingEnabled); }
            // data
            const res = await apiFetch('/api/tenant/self/branding', {
                headers: { 'Accept': 'application/json', ...(etag ? { 'If-None-Match': etag } : {}) }
            });
            if (res.status === 304) return;
            if (res.status === 204) { setBranding(null); return; }
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const data = await res.json();
            setBranding(data);
            const newE = res.headers.get('ETag'); if (newE) setEtag(newE);
        } catch (e: any) {
            setError(e.message || 'Errore caricamento');
        } finally { setLoading(false); }
    }, [etag]);

    useEffect(() => { load(); }, [load]);

    if (!enabled) return <div className="card"><h3>Branding</h3><div>Questa funzionalità è disabilitata.</div></div>;

    return (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
            <h3>Branding</h3>
            <Suspense fallback={<div>Caricamento...</div>}>
                {!editing && (
                    <BrandPreview branding={branding} loading={loading} error={error} onEdit={() => setEditing(true)} />
                )}
                {editing && (
                    <BrandEditor
                        initial={branding || undefined}
                        onCancel={() => setEditing(false)}
                        onSave={async (draft) => {
                            try {
                                const { apiFetch } = await import('../api/client');
                                // 1) se presenti file, caricali e ottieni gli URL
                                async function upload(f?: File | null): Promise<string | undefined> {
                                    if (!f) return undefined;
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    const upRes = await apiFetch('/api/upload', { method: 'POST', body: fd });
                                    if (!upRes.ok) throw new Error('Upload fallito');
                                    const upJson = await upRes.json();
                                    return upJson.url as string;
                                }
                                const [logoUploadedUrl, bgUploadedUrl] = await Promise.all([
                                    upload(draft.logoFile),
                                    upload(draft.backgroundFile)
                                ]);


                                const payload: TenantBranding = {
                                    name: draft.name.trim(),
                                    description: draft.description?.trim() || undefined,
                                    primaryColor: draft.primaryColor,
                                    secondaryColor: draft.secondaryColor,
                                    backgroundColor: draft.backgroundColor,
                                    textColor: draft.textColor,
                                    fontFamily: draft.fontFamily,
                                    logoUrl: logoUploadedUrl ?? branding?.logoUrl,
                                    backgroundImageUrl: bgUploadedUrl ?? branding?.backgroundImageUrl
                                };
                                // 2) salva branding
                                const res = await apiFetch('/api/tenant/self/branding', {
                                    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
                                });
                                if (!res.ok) {
                                    let message = 'Salvataggio fallito';
                                    try {
                                        const text = await res.text();
                                        if (text) {
                                            try {
                                                const parsed = JSON.parse(text);
                                                message = parsed.message || parsed.error || text;
                                            } catch {
                                                message = text;
                                            }
                                        }
                                    } catch {
                                        // ignore parsing errors
                                    }
                                    throw new Error(message);
                                }
                                const saved = await res.json();
                                setBranding(saved);
                                const newE = res.headers.get('ETag'); if (newE) setEtag(newE);
                                setEditing(false);
                            } catch (e: any) {
                                setError(e.message || 'Errore salvataggio');
                            }
                        }}
                    />
                )}
            </Suspense>
        </div>
    );
}
